// Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved.

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	Self,
} from '@angular/core';
import { NgControl, ValidatorFn } from '@angular/forms';
import { Observable, combineLatest, BehaviorSubject } from 'rxjs';
import { map, takeUntil, startWith } from 'rxjs/operators';
import { all_words_in_list, regexEmptyString } from 'src/app/app-data/constants/regex-patterns';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { MultiWrapperComponent } from 'src/app/modules/ticket-edit/components/multi-wrapper/multi-wrapper.component';
import { TicketEditBaseComponent } from 'src/app/modules/ticket-edit/components/ticket-edit-base/ticket-edit-base.component';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { maintainParentErrorParity, maintainParentRequiredParity } from '../../custom-field-utils';
import { validCustomLink } from '../../../../common/form-utils/validators';

@Component({
	selector: 'custom-field-link',
	templateUrl: './custom-field-link.component.html',
	styleUrls: ['./custom-field-link.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFieldLinkComponent extends TicketEditBaseComponent implements OnInit, OnDestroy {
	@Input() public customField: CustomFieldDefinition = new CustomFieldDefinition('');

	private _allowList$ = new BehaviorSubject<string[]>([]);
	@Input()
	set allowList(value: string[] | null) {
		if (value) {
			this._allowList$.next(value);
		}
	}

	@Input() public planId = '';
	@Input() public ticketId = '';
	public disableOpenLinkButton$!: Observable<boolean>;

	constructor(
		@Self() @Optional() public override control: NgControl,
		@Optional() public override multiWrapper: MultiWrapperComponent,
		@Optional() protected override cd: ChangeDetectorRef
	) {
		super(control, multiWrapper, cd);
	}

	public override ngOnInit() {
		super.ngOnInit();

		this.disableOpenLinkButton$ = combineLatest({
			value: this.internalField.valueChanges.pipe(startWith(this.internalField.value)),
			allowList: this._allowList$,
		}).pipe(
			map(({ value, allowList }) => {
				if (allowList && allowList.length) {
					const regex = all_words_in_list(allowList);
					return !regex.test(value);
				} else {
					return true;
				}
			})
		);

		maintainParentErrorParity(this.control.control, this.internalField, this.shutdownSubject);
		maintainParentRequiredParity(this.control.control, this.internalField, this.shutdownSubject);

		let currentValidator: ValidatorFn;
		this._allowList$.pipe(takeUntil(this.shutdownSubject)).subscribe((allowList) => {
			if (allowList && allowList.length) {
				// if allowList exists, check input string against allowed domains
				const regex = all_words_in_list(allowList);
				if (currentValidator) {
					this.internalField.removeValidators(currentValidator);
				}
				currentValidator = validCustomLink(regex);
				this.internalField.addValidators(currentValidator);
				this.control.control?.addValidators(currentValidator);
				this.internalField.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				this.control.control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
			} else {
				if (currentValidator) {
					this.internalField.removeValidators([currentValidator]);
				}
				currentValidator = validCustomLink(regexEmptyString);
				// if allowlist doesn't exist, any input strings will trigger error
				this.internalField.addValidators(currentValidator);
				this.control.control?.addValidators(currentValidator);
				this.internalField.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				this.control.control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
			}
		});

		this.internalField.valueChanges.pipe(takeUntil(this.shutdownSubject)).subscribe((value: string) => {
			this.onChange(value);
		});
	}

	public openLinkInNewTab() {
		window.open(this.control.value, '_blank');
		analyticsEventsService.followHyperlink(this.planId, this.ticketId, this.customField.$id);
	}
}
